import { createAction } from "@reduxjs/toolkit";
import { actions as routerActions } from "redux-router5";
import type { UploadRequestError } from "../../../node_modules/@rc-component/upload/lib/interface.ts";
import { client } from "../../app/api-client.ts";
import { type AppDispatch, listener, type RootState } from "../../app/store.ts";
import { responseUnwrap } from "../../common/functions.ts";
import { ticketActions } from "../../features/ticket/ticketSlice.ts";
import { routeNames } from "../common/routes.ts";
import type {
  Attachment,
  ModelFile,
  PatchedFileRelations,
  TicketRetrieve,
} from "../generated-axios-client/index.ts";

async function refreshTicketFiles(ticketId: string, dispatch: AppDispatch) {
  const [ticketResponse, filesResponse] = await Promise.all([
    client.apiV1ManagementTicketRetrieve(ticketId),
    client.apiV1ManagementFilesList(undefined, undefined, ticketId),
  ]);

  const unwrappedTicket = responseUnwrap<TicketRetrieve>(ticketResponse);
  if (!("error" in unwrappedTicket)) {
    dispatch(ticketActions.storeTicket(unwrappedTicket));
  }

  const unwrappedFiles = responseUnwrap(filesResponse);
  if (!("error" in unwrappedFiles)) {
    dispatch(ticketActions.storeTicketFiles(unwrappedFiles.results as ModelFile[]));
  }
}

type StartViewUploadOptions = {
  file: File;
  ticketId: string;
  bladeId?: string;
  ticketItemId?: string;
  onSuccess?: () => void;
  onError?: () => void;
};

export const startViewUpload = createAction<StartViewUploadOptions>(
  "file/START_VIEW_UPLOAD"
);

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: startViewUpload,
  effect: async (action, { dispatch }) => {
    const { file, ticketId, ticketItemId, onSuccess, onError } = action.payload;
    const metadata: Attachment = {
      name: file.name,
      size: file.size,
      content_type: file.type,
      ticket: ticketId,
      ticket_item: ticketItemId,
    };
    try {
      const response = await client.apiV1ManagementFilesCreate(
        file as unknown as string,
        metadata
      );
      const unwrapped = responseUnwrap<ModelFile>(response);
      if ("error" in unwrapped) {
        onError?.();
        return;
      }
      onSuccess?.();
      await refreshTicketFiles(ticketId, dispatch);
    } catch {
      onError?.();
    }
  },
});

export const deleteFile = createAction("file/DELETE", (fileId: string) => ({
  payload: { fileId },
}));

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: deleteFile,
  effect: async (action, { dispatch, getState }) => {
    const { fileId } = action.payload;
    await client.apiV1ManagementFilesDestroy(fileId);

    const ticketId = getState().ticket.ticket?.id;
    if (ticketId) {
      await refreshTicketFiles(ticketId, dispatch);
    }
  },
});

type UpdateFileRelationOptions = {
  relationId: string;
  description: string | null;
};

export const updateFileRelation = createAction<UpdateFileRelationOptions>(
  "file/UPDATE_FILE_RELATION"
);

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: updateFileRelation,
  effect: async (action, { dispatch, getState }) => {
    const { relationId, description } = action.payload;
    const patch: PatchedFileRelations = { description };
    const response = await client.apiV1ManagementFileRelationsPartialUpdate(
      relationId,
      patch
    );
    const unwrapped = responseUnwrap(response);
    if (!("error" in unwrapped)) {
      const ticketId = getState().ticket.ticket?.id;
      if (ticketId) {
        await refreshTicketFiles(ticketId, dispatch);
      }
    }
  },
});

export const startUpload = createAction(
  "file/START_UPLOAD",
  (
    file: File,
    onSuccess?: (body: ModelFile, xhr?: XMLHttpRequest) => void,
    onError?: (event: UploadRequestError) => void
  ) => {
    return {
      payload: {
        file,
        onError,
        onSuccess,
      },
    };
  }
);

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: startUpload,
  effect: async (action, { dispatch, getState }) => {
    const { file, onSuccess, onError } = action.payload;
    const state = getState();
    const metadata = {
      name: file.name,
      size: file.size,
      content_type: file.type,
    };
    const response = await client.apiV1ManagementFilesCreate(
      file as unknown as string,
      metadata
    );
    const unwrappedFileResponse = responseUnwrap<ModelFile>(response);
    if ("error" in unwrappedFileResponse) {
      if (onError) {
        onError(new Error(unwrappedFileResponse.error));
      }
    } else {
      if (onSuccess) {
        onSuccess(unwrappedFileResponse);
        const currentRoute = state.router.route;
        if (currentRoute?.name === routeNames.TICKET_VIEW) {
          dispatch(
            routerActions.navigateTo(currentRoute.name, currentRoute.params, {
              replace: true,
              force: true,
            })
          );
        }
      }
    }
  },
});
